import { useRef, useState, useEffect } from "react";
import { Icons } from "@/shared";
import { IconAvatar } from "@/shared/icons";
import { IconTrash } from "@/shared/icons/IconTrash";
import { useToastStore } from "@/shared/stores/toastStore";
import {
  AvatarUploadWrapper,
  AvatarCircle,
  AvatarImageWrapper,
  AvatarPreview,
  AvatarOverlay,
  PencilButton,
  HiddenInput,
} from "./AvatarUpload.styles";

const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;
const DEFAULT_EXTENSIONS: readonly string[] = [".jpg", ".jpeg", ".png"];
const ACCEPT_STRING = "image/jpeg,image/png";

const VALIDATION_ERROR_MESSAGE = "Solo formatos JPG, JPEG o PNG hasta 5MB";

export interface AvatarUploadProps {
  readonly value: File | null;
  readonly onChange: (file: File) => void;
  readonly onRemove?: () => void;
  readonly currentImageUrl?: string | null;
  readonly allowRemoveCurrentImage?: boolean;
  readonly maxSize?: number;
  readonly acceptExtensions?: readonly string[];
  readonly ariaLabel?: string;
}

export const AvatarUpload = ({
  value,
  onChange,
  onRemove,
  currentImageUrl,
  allowRemoveCurrentImage = false,
  maxSize = DEFAULT_MAX_SIZE,
  acceptExtensions = DEFAULT_EXTENSIONS,
  ariaLabel = "Subir foto de perfil",
}: AvatarUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const showToast = useToastStore((s) => s.showToast);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(currentImageUrl ?? null);
      return;
    }

    const url = URL.createObjectURL(value);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [currentImageUrl, value]);

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const hasValidExtension = (fileName: string): boolean => {
    const lowerName = fileName.toLowerCase();
    return acceptExtensions.some((ext) => lowerName.endsWith(ext));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    // Resetear el input para permitir re-selección del mismo archivo
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    if (!file) return;

    if (!hasValidExtension(file.name) || file.size > maxSize) {
      showToast({ type: "error", title: VALIDATION_ERROR_MESSAGE, duration: 3000 });
      return;
    }

    onChange(file);
  };

  return (
    <AvatarUploadWrapper
      onClick={openFilePicker}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openFilePicker();
        }
      }}
    >
      <AvatarCircle>
        {previewUrl ? (
          <AvatarImageWrapper>
            <AvatarPreview src={previewUrl} alt="Foto del paciente" />
            {(value || (allowRemoveCurrentImage && currentImageUrl)) && onRemove ? (
              <AvatarOverlay
                data-overlay
                type="button"
                aria-label="Eliminar foto"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <IconTrash width={40} height={40} color="white" />
              </AvatarOverlay>
            ) : null}
          </AvatarImageWrapper>
        ) : (
          <IconAvatar />
        )}
      </AvatarCircle>

      <PencilButton
        type="button"
        aria-label="Cambiar foto"
        onClick={(e) => {
          e.stopPropagation();
          openFilePicker();
        }}
      >
        <Icons name="iconPencil" width={16} height={16} />
      </PencilButton>

      <HiddenInput
        ref={inputRef}
        type="file"
        accept={ACCEPT_STRING}
        onChange={handleFileChange}
        tabIndex={-1}
        data-testid="avatar-upload-input"
      />
    </AvatarUploadWrapper>
  );
};
